import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAdminStats } from "@/context/AdminStatsContext";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import {
  Files,
  Users,
  UserPlus,
  Car as CarIcon,
  Buildings,
  Tag,
  ChartLineUp,
  Calculator,
  Bell,
  BookOpen,
  ChartBar,
  ShieldCheck,
  ClipboardText,
  ClockCountdown,
  Wallet,
  CurrencyCircleDollar,
  TrendUp,
  ArrowLeft,
  GearSix,
  Crown,
  Coins,
  Clock,
  Trash,
  FolderOpen,
} from "@phosphor-icons/react";

// The admin toolkit grid (matches the Expo app layout). Titles/subtitles come
// from i18n so switching language re-labels every card immediately.
const ADMIN_TOOL_DEFS = [
  { to: "/company-bookings", icon: ClipboardText, key: "bookings" },
  { to: "/admin/drivers", icon: Users, key: "drivers" },
  { to: "/admin/external-drivers", icon: UserPlus, key: "external" },
  { to: "/admin/cars", icon: CarIcon, key: "cars" },
  { to: "/admin/companies", icon: Buildings, key: "companies" },
  { to: "/admin/route-pricing", icon: Tag, key: "prices" },
  { to: "/admin/pricing-templates", icon: FolderOpen, key: "pricingTemplates" },
  { to: "/admin/driver-wage-rules", icon: Coins, key: "wages" },
  { to: "/admin/departure-rules", icon: Clock, key: "departure" },
  { to: "/admin/statements", icon: ChartLineUp, key: "statements" },
  { to: "/admin/car-revenue", icon: CurrencyCircleDollar, key: "carRevenue" },
  { to: "/admin/car-profit", icon: TrendUp, key: "carProfit" },
  { to: "/admin/finance", icon: Calculator, key: "finance" },
  { to: "/admin/notifications", icon: Bell, key: "notifications" },
  { to: "/admin/help", icon: BookOpen, key: "help" },
  { to: "/admin/analytics", icon: ChartBar, key: "analytics" },
];

function StatCard({ icon: Icon, label, value, tone = "gold", loading, testId }) {
  const toneClass =
    tone === "red"
      ? "text-[#F87171] border-[#F87171]/30 bg-[#F87171]/5"
      : tone === "green"
      ? "text-[#4ADE80] border-[#4ADE80]/30 bg-[#4ADE80]/5"
      : "text-primary border-primary/30 bg-primary/5";
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl p-4 md:p-5 border ${toneClass} flex items-center gap-3 fadeup`}
    >
      <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
        <Icon size={22} weight="duotone" className={toneClass.split(" ")[0]} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white/60 text-xs">{label}</div>
        {loading ? (
          <div className="h-7 w-16 mt-1 rounded bg-white/5 animate-pulse" />
        ) : (
          <div className={`text-2xl font-bold num ${toneClass.split(" ")[0]}`}>{value}</div>
        )}
      </div>
    </div>
  );
}

function AdminToolCard({ to, icon: Icon, title, subtitle, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="fadeup group flex flex-col items-center justify-center text-center gap-2 rounded-2xl p-4 border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] hover:border-primary/70 hover:shadow-[0_0_0_1px_var(--primary)] transition-all min-h-[140px]"
    >
      <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center group-hover:bg-primary/25 transition-all">
        <Icon size={26} weight="duotone" className="text-primary" />
      </div>
      <div className="text-white font-bold text-sm">{title}</div>
      <div className="text-white/50 text-[11px]">{subtitle}</div>
    </Link>
  );
}

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  // Only the general manager (abugena01@gmail.com + admin_type=general_manager)
  // sees the sensitive admin tools (إدارة الأدمن + الإعدادات).
  const isGeneralManager =
    (user?.email || "").toLowerCase() === "abugena01@gmail.com" &&
    user?.admin_type === "general_manager";
  const { stats, loading: statsLoading } = useAdminStats();
  const [finance, setFinance] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      setFinanceLoading(true);
      try {
        const { data } = await api.get("/finance/summary");
        if (!cancelled) setFinance(data);
      } catch (e) {
        console.warn("[AdminPanel] /finance/summary failed:", e?.response?.status || e?.message);
      } finally {
        if (!cancelled) setFinanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  const loading = statsLoading && !stats;
  const finLoading = financeLoading && !finance;

  const totalRevenue =
    finance?.total_revenue ??
    finance?.total ??
    (stats?.latest_bookings?.reduce((s, b) => s + Number(b?.total_price || 0), 0) || 0);
  const driverPayouts = finance?.total_wages ?? finance?.driver_payouts ?? 0;
  const agentCommission = finance?.total_commission ?? finance?.agent_commission ?? 0;
  const netProfit = finance?.net_profit ?? (Number(totalRevenue) - Number(driverPayouts) - Number(agentCommission));

  const adminTools = useMemo(
    () =>
      ADMIN_TOOL_DEFS.map((tool) => ({
        ...tool,
        title: t(`admin.tools.${tool.key}`),
        subtitle: t(`admin.tools.${tool.key}Sub`),
      })),
    [t],
  );

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="px-4 py-6" data-testid="admin-panel">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Title */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{t("admin.panelTitle")}</h1>
              <p className="text-white/50 text-sm mt-0.5">{t("admin.panelSubtitle")}</p>
            </div>
          </div>

          {/* Quick stats: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard
              icon={Files}
              label={t("admin.statTotalBookings")}
              value={fmtNum(stats?.total_bookings ?? 0)}
              tone="gold"
              loading={loading}
              testId="stat-total-bookings"
            />
            <StatCard
              icon={ClockCountdown}
              label={t("admin.statConfirmed")}
              value={fmtNum(stats?.confirmed_bookings ?? 0)}
              tone="green"
              loading={loading}
              testId="stat-confirmed-bookings"
            />
            <StatCard
              icon={Buildings}
              label={t("admin.statCompanies")}
              value={fmtNum(stats?.total_companies ?? 0)}
              tone="green"
              loading={loading}
              testId="stat-total-companies"
            />
          </div>

          {/* Financial summary */}
          <div
            data-testid="admin-finance-summary"
            className="rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 md:p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Wallet size={22} weight="duotone" className="text-primary" />
              <h2 className="text-lg md:text-xl font-bold text-white">{t("admin.financeTitle")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="rounded-xl bg-[#0B1A3A] border border-primary/20 p-4">
                <div className="text-white/60 text-xs mb-1">{t("admin.financeRevenue")}</div>
                {finLoading ? (
                  <div className="h-7 w-24 rounded bg-white/5 animate-pulse" />
                ) : (
                  <div className="text-2xl font-bold text-primary num">{fmtRiyal(totalRevenue)}</div>
                )}
              </div>
              <div className="rounded-xl bg-[#0B1A3A] border border-[#F87171]/25 p-4">
                <div className="text-white/60 text-xs mb-1">{t("admin.financePayouts")}</div>
                {finLoading ? (
                  <div className="h-7 w-24 rounded bg-white/5 animate-pulse" />
                ) : (
                  <div className="text-2xl font-bold text-[#F87171] num">{fmtRiyal(driverPayouts)}</div>
                )}
              </div>
              {/* 2026-07 §AgentCommissionCard */}
              <div className="rounded-xl bg-[#0B1A3A] border border-[#38BDF8]/25 p-4" data-testid="finance-commission-card">
                <div className="text-white/60 text-xs mb-1">عمولة المناديب</div>
                {finLoading ? (
                  <div className="h-7 w-24 rounded bg-white/5 animate-pulse" />
                ) : (
                  <div className="text-2xl font-bold text-[#38BDF8] num">{fmtRiyal(agentCommission)}</div>
                )}
              </div>
              <div className="rounded-xl bg-[#0B1A3A] border border-[#4ADE80]/25 p-4">
                <div className="text-white/60 text-xs mb-1 flex items-center gap-1">
                  <CurrencyCircleDollar size={12} /> {t("admin.financeNet")}
                </div>
                {finLoading ? (
                  <div className="h-7 w-24 rounded bg-white/5 animate-pulse" />
                ) : (
                  <div className="text-2xl font-bold text-[#4ADE80] num">{fmtRiyal(netProfit)}</div>
                )}
              </div>
            </div>
            <div className="text-white/40 text-[11px] mt-3 text-center">
              محسوب تلقائياً: الإيرادات − أجور السائقين − عمولة المناديب.
            </div>
          </div>

          {/* Admin tools grid — 9 cards */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg md:text-xl font-bold text-white">{t("admin.toolsTitle")}</h2>
              <ArrowLeft size={16} className={`text-primary/60 hidden md:inline ${isEn ? "rotate-180" : ""}`} />
            </div>
            <div
              data-testid="admin-tools-grid"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3"
            >
              {adminTools.map((tool) => (
                <AdminToolCard
                  key={tool.to}
                  {...tool}
                  testId={`tool-${tool.to.replace(/\//g, "-").replace(/^-/, "")}`}
                />
              ))}
              {isGeneralManager && (
                <>
                  <AdminToolCard
                    to="/admin/team"
                    icon={Crown}
                    title={t("admin.tools.team")}
                    subtitle={t("admin.tools.teamSub")}
                    testId="tool-admin-team"
                  />
                  <AdminToolCard
                    to="/admin/settings"
                    icon={GearSix}
                    title={t("admin.tools.settings")}
                    subtitle={t("admin.tools.settingsSub")}
                    testId="tool-admin-settings"
                  />
                  <Link
                    to="/admin/data-reset"
                    data-testid="tool-admin-data-reset"
                    className="fadeup group flex flex-col items-center justify-center text-center gap-2 rounded-2xl p-4 border border-red-500/40 bg-gradient-to-br from-red-950/50 to-red-900/30 hover:border-red-400/70 hover:shadow-[0_0_0_1px_rgb(239,68,68)] transition-all min-h-[140px]"
                  >
                    <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center group-hover:bg-red-500/25 transition-all">
                      <Trash size={26} weight="duotone" className="text-red-400" />
                    </div>
                    <div className="text-red-300 font-bold text-sm">{t("admin.tools.dataReset")}</div>
                    <div className="text-red-200/50 text-[11px] leading-tight">{t("admin.tools.dataResetSub")}</div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
